import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Seo from './Seo';
import Footer from './Footer';
import AuthModal from './AuthModal';
import { ProductIcon, productTint } from './productIcons';
import { useStudioTheme } from '../hooks/useStudioTheme';
import './NeuralHomepage.css';
import './NeuralHomepage.theme.css';

const HERO_IMG = '/images/home/hero-career-copilot.png';

/* ---------- Three.js neural-orb canvas ----------
 * THREE is dynamically imported inside the effect so it's split into its own
 * chunk and doesn't block the homepage's first paint. The canvas pops in
 * once the bundle arrives — typically a few hundred ms after the rest of the
 * hero is interactive. */
const HeroNeuralCanvas = () => {
  const mountRef = useRef(null);
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    let cancelled = false;
    let cleanup = () => {};

    import('three').then((THREE) => {
      if (cancelled || !mount) return;

    let width = mount.clientWidth;
    let height = mount.clientHeight;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
    camera.position.z = 80;
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    const sprite = (() => {
      const c = document.createElement('canvas');
      c.width = c.height = 64;
      const ctx = c.getContext('2d');
      const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
      g.addColorStop(0, 'rgba(255,255,255,1)');
      g.addColorStop(0.35, 'rgba(224,163,255,0.95)');
      g.addColorStop(0.7, 'rgba(255,105,180,0.4)');
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, 64, 64);
      return new THREE.CanvasTexture(c);
    })();

    const RINGS = [
      { count: 18, radius: 22, speed: 0.20, tilt: 0.0 },
      { count: 26, radius: 36, speed: -0.14, tilt: 0.5 },
      { count: 34, radius: 50, speed: 0.10, tilt: -0.4 },
    ];
    const ringObjs = RINGS.map((spec) => {
      const positions = new Float32Array(spec.count * 3);
      const angles = new Float32Array(spec.count);
      for (let i = 0; i < spec.count; i++) angles[i] = (i / spec.count) * Math.PI * 2;
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      const mat = new THREE.PointsMaterial({
        size: 2.2, map: sprite, transparent: true,
        depthWrite: false, blending: THREE.AdditiveBlending, color: 0xffffff,
      });
      const pts = new THREE.Points(geo, mat);
      scene.add(pts);
      return { spec, geo, mat, angles, pts };
    });

    const fieldCount = 200;
    const fieldPos = new Float32Array(fieldCount * 3);
    const fieldSpeeds = new Float32Array(fieldCount);
    for (let i = 0; i < fieldCount; i++) {
      fieldPos[i * 3] = (Math.random() - 0.5) * 200;
      fieldPos[i * 3 + 1] = (Math.random() - 0.5) * 120;
      fieldPos[i * 3 + 2] = (Math.random() - 0.5) * 70;
      fieldSpeeds[i] = 0.04 + Math.random() * 0.10;
    }
    const fieldGeo = new THREE.BufferGeometry();
    fieldGeo.setAttribute('position', new THREE.BufferAttribute(fieldPos, 3));
    const fieldMat = new THREE.PointsMaterial({
      size: 0.8, map: sprite, transparent: true,
      depthWrite: false, blending: THREE.AdditiveBlending,
      color: 0xffffff, opacity: 0.40,
    });
    const field = new THREE.Points(fieldGeo, fieldMat);
    scene.add(field);

    const coreGeo = new THREE.IcosahedronGeometry(8, 1);
    const core = new THREE.Mesh(coreGeo, new THREE.MeshBasicMaterial({
      color: 0xe0a3ff, wireframe: true, transparent: true, opacity: 0.40,
    }));
    scene.add(core);

    let raf;
    let mx = 0, my = 0, t = 0;
    const onMove = (e) => {
      const r = mount.getBoundingClientRect();
      mx = ((e.clientX - r.left) / r.width - 0.5) * 2;
      my = ((e.clientY - r.top) / r.height - 0.5) * 2;
    };
    window.addEventListener('mousemove', onMove);
    const onResize = () => {
      width = mount.clientWidth; height = mount.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    window.addEventListener('resize', onResize);

    const animate = () => {
      t += 0.01;
      ringObjs.forEach(({ spec, geo, angles }) => {
        const pos = geo.attributes.position.array;
        for (let i = 0; i < spec.count; i++) {
          const a = angles[i] + t * spec.speed;
          pos[i * 3]     = Math.cos(a) * spec.radius;
          pos[i * 3 + 1] = Math.sin(a) * spec.radius * Math.cos(spec.tilt);
          pos[i * 3 + 2] = Math.sin(a) * spec.radius * Math.sin(spec.tilt);
        }
        geo.attributes.position.needsUpdate = true;
      });
      const fp = fieldGeo.attributes.position.array;
      for (let i = 0; i < fieldCount; i++) {
        fp[i * 3 + 1] += fieldSpeeds[i] * 0.5;
        if (fp[i * 3 + 1] > 60) fp[i * 3 + 1] = -60;
      }
      fieldGeo.attributes.position.needsUpdate = true;
      core.rotation.x += 0.005; core.rotation.y += 0.006;
      camera.position.x += (mx * 6 - camera.position.x) * 0.04;
      camera.position.y += (-my * 4 - camera.position.y) * 0.04;
      camera.lookAt(0, 0, 0);
      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

      // Capture the scene cleanup so the outer effect's return can run it
      // when the component unmounts (which may happen before or after the
      // dynamic import resolves).
      cleanup = () => {
        cancelAnimationFrame(raf);
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('resize', onResize);
        ringObjs.forEach(({ geo, mat }) => { geo.dispose(); mat.dispose(); });
        fieldGeo.dispose(); fieldMat.dispose();
        coreGeo.dispose(); core.material.dispose();
        sprite.dispose();
        renderer.dispose();
        if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
      };
    });

    return () => {
      cancelled = true;
      cleanup();
    };
  }, []);
  return <div ref={mountRef} className="nh-hero-canvas" aria-hidden="true" />;
};

/* ---------- Floating product tiles around the hero ---------- */
const FLOATING_PRODUCTS = [
  // left column (top → bottom) — kept clear of the floating header at the top
  { side: 'left', y: '32%',  name: 'Interview Assistant', path: '/InterviewAssistant' },
  { side: 'left', y: '44%',  name: 'Mock Interview',      path: '/mockInterview' },
  { side: 'left', y: '56%',  name: 'Resume Builder',      path: '/resume-builder' },
  { side: 'left', y: '68%',  name: 'Cover Letter',        path: '/CoverLetter' },
  { side: 'left', y: '80%',  name: 'ATS Check',           path: '/ATSCheck' },
  // right column
  { side: 'right', y: '32%', name: 'Interview Questions', path: '/InterviewQuestions' },
  { side: 'right', y: '44%', name: 'LinkedIn Optimizer',  path: '/LinkedInOptimizer' },
  { side: 'right', y: '56%', name: 'Auto Apply',          path: '/AutoJobApply' },
  { side: 'right', y: '68%', name: 'Portfolio Builder',   path: '/portfolio' },
  { side: 'right', y: '80%', name: 'Course Creator',      path: '/certification' },
];

const FloatingProducts = ({ onNav }) => (
  <div className="nh-floats" aria-hidden="false">
    {FLOATING_PRODUCTS.map((p, i) => (
      <button
        key={p.name}
        type="button"
        onClick={() => onNav(p.path)}
        className={`nh-float nh-float-${p.side}`}
        style={{ top: p.y, '--nh-i': i, '--tint': productTint(p.path) }}
      >
        <span className="nh-float-icon" aria-hidden="true">
          <ProductIcon path={p.path} size={14} />
        </span>
        <span className="nh-float-name">{p.name}</span>
      </button>
    ))}
  </div>
);

/* ---------- Terview app overlay demo (sits on the hero video) ----------
   Cycles through real interview questions — behavioural, coding, system
   design, logic — streaming each answer the way the live assistant does.
   Draggable, like the actual desktop overlay. */
const DEMO_QA = [
  {
    tag: 'BEHAVIOURAL',
    q: 'Tell me about a time you led a difficult project.',
    a: 'I led our monolith-to-microservices migration — cut deploy time 40% and lifted uptime to 99.9%, coordinating three teams over two quarters.',
    chips: ['Shorter', 'Add example', 'Use STAR'],
  },
  {
    tag: 'CODING',
    q: 'Reverse a linked list in place.',
    a: 'Walk the list with three pointers, flipping each link as you go — O(n) time, O(1) space.',
    code: 'while (curr) {\n  const next = curr.next;\n  curr.next = prev;\n  prev = curr;\n  curr = next;\n}\nreturn prev;',
    chips: ['Explain complexity', 'Edge cases', 'In Python'],
  },
  {
    tag: 'SYSTEM DESIGN',
    q: 'How would you design a URL shortener?',
    a: 'Base62-encode an auto-increment ID, cache hot keys in Redis, and shard the mapping table by hash prefix.',
    chips: ['Go deeper', 'Trade-offs', 'Draw it'],
  },
  {
    tag: 'LOGIC',
    q: 'Why are manhole covers round?',
    a: 'A circle has constant width, so the cover can never fall through its own opening at any angle — and it rolls instead of being carried.',
    chips: ['Shorter', 'More technical'],
  },
];

const HeroAppOverlay = () => {
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const drag = useRef({ active: false, sx: 0, sy: 0, ox: 0, oy: 0 });

  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState('thinking'); // thinking → typing → hold
  const [chars, setChars] = useState(0);
  // Starts collapsed so the video reads on its own, then the panel slides in
  // after a beat — mimics the assistant popping up mid-interview.
  const [collapsed, setCollapsed] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setCollapsed(false), 3000);
    return () => clearTimeout(t);
  }, []);

  const item = DEMO_QA[idx];

  // Reset + "thinking" beat whenever the question changes. Gated on `collapsed`
  // so the demo doesn't silently advance while the panel is hidden — it starts
  // fresh from the generating state the moment the card appears.
  useEffect(() => {
    if (collapsed) return undefined;
    setPhase('thinking');
    setChars(0);
    const t = setTimeout(() => setPhase('typing'), 900);
    return () => clearTimeout(t);
  }, [idx, collapsed]);

  // Stream the answer out character by character
  useEffect(() => {
    if (collapsed || phase !== 'typing') return undefined;
    const total = item.a.length;
    const id = setInterval(() => {
      setChars((c) => {
        if (c >= total) { clearInterval(id); setPhase('hold'); return c; }
        return c + 2;
      });
    }, 16);
    return () => clearInterval(id);
  }, [phase, item.a, collapsed]);

  // Hold the finished answer, then advance
  useEffect(() => {
    if (collapsed || phase !== 'hold') return undefined;
    const t = setTimeout(() => setIdx((i) => (i + 1) % DEMO_QA.length), 3200);
    return () => clearTimeout(t);
  }, [phase, collapsed]);

  // Drag — listeners on window so the gesture survives leaving the element
  const onDown = (e) => {
    e.preventDefault();
    drag.current = { active: true, sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y };
    setDragging(true);
  };
  useEffect(() => {
    const move = (e) => {
      if (!drag.current.active) return;
      setPos({
        x: drag.current.ox + (e.clientX - drag.current.sx),
        y: drag.current.oy + (e.clientY - drag.current.sy),
      });
    };
    const up = () => {
      if (!drag.current.active) return;
      drag.current.active = false;
      setDragging(false);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
  }, []);

  const shown = item.a.slice(0, chars);
  const done = phase === 'hold';

  return (
    <div
      className={`nh-app ${dragging ? 'is-dragging' : ''}`}
      aria-hidden="true"
      style={{ transform: `translate(${pos.x}px, ${pos.y}px)` }}
      onMouseDown={onDown}
    >
      <div className="nh-app-bar">
        <span className="nh-app-logo">T</span>
        <span
          className={`nh-app-hide ${collapsed ? 'is-collapsed' : ''}`}
          /* stopPropagation so clicking the button doesn't start a drag */
          onMouseDown={(e) => e.stopPropagation()}
          onClick={() => setCollapsed((c) => !c)}
        >
          <svg width="11" height="11" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6"/></svg>
          {collapsed ? 'Show' : 'Hide'}
        </span>
        <span className="nh-app-stop" />
        <span className="nh-app-timer">29:12</span>
      </div>

      {!collapsed && (
      <div className="nh-app-card">
        <div className="nh-app-askrow">
          <span className="nh-app-tag">{item.tag}</span>
          <span className="nh-app-ask">{item.q}</span>
        </div>

        {phase === 'thinking' ? (
          <div className="nh-app-thinking">
            <span className="nh-app-thinking-label">Terview is generating an answer</span>
            <span className="nh-app-dots"><i /><i /><i /></span>
          </div>
        ) : (
          <>
            <p className="nh-app-answer">
              {shown}
              {!done && <span className="nh-app-caret" />}
            </p>
            {item.code && done && (
              <pre className="nh-app-code">{item.code}</pre>
            )}
          </>
        )}

        <div className="nh-app-chips">
          {item.chips.map((c) => (
            <span className="nh-app-chip" key={c}>{c}</span>
          ))}
        </div>

        <div className="nh-app-input">
          <span className="nh-app-placeholder">Ask about your screen or conversation…</span>
          <span className="nh-app-send">
            <svg width="11" height="11" viewBox="0 0 24 24"><path fill="currentColor" d="M3 20.5v-6l8-2.5-8-2.5v-6L22 12z"/></svg>
          </span>
        </div>
      </div>
      )}
    </div>
  );
};

/* ---------- Hero ---------- */
const Hero = ({ onCTA, onNav }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      el.style.setProperty('--mx', x);
      el.style.setProperty('--my', y);
    };
    el.addEventListener('mousemove', onMove);
    return () => el.removeEventListener('mousemove', onMove);
  }, []);


  return (
    <section className="nh-hero" ref={ref}>
      <HeroNeuralCanvas />
      <div className="nh-hero-bg" />
      <div className="nh-hero-halo" aria-hidden="true" />
      <img src={HERO_IMG} alt="" className="nh-hero-img" loading="eager" />

      <FloatingProducts onNav={onNav} />

      <div className="nh-hero-content">
        <h1 className="nh-h1">
          Ace Your <span className="nh-grad-text">Dream Job</span>.
        </h1>
        <div className="nh-hero-cta">
          <button className="nh-btn nh-btn-primary" onClick={onCTA}>
            Start free
            <svg width="16" height="16" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7"/></svg>
          </button>
          <a className="nh-btn nh-btn-ghost" href="#features">Explore features</a>
        </div>

        {/* Product demo — muted + playsInline so browsers allow autoplay */}
        <div className="nh-hero-video">
          <video
            className="nh-hero-video-el"
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            aria-label="Terview interview assistant demo"
          >
            <source src="/videos/Interview.mp4" type="video/mp4" />
          </video>

          {/* Terview desktop-app overlay — live answer demo, draggable */}
          <HeroAppOverlay />
        </div>

        <div className="nh-hero-proof">
          <div className="nh-proof-item">
            <div className="nh-proof-stars">★★★★★</div>
            <div className="nh-proof-meta">4.8 · 2,739 reviews</div>
          </div>
          <div className="nh-proof-divider" />
          <div className="nh-proof-item">
            <div className="nh-proof-num">1M+</div>
            <div className="nh-proof-meta">Visits</div>
          </div>
          <div className="nh-proof-divider" />
          <div className="nh-proof-item">
            <div className="nh-proof-num">116ms</div>
            <div className="nh-proof-meta">Avg latency</div>
          </div>
          <div className="nh-proof-divider" />
          <div className="nh-proof-item">
            <div className="nh-proof-num">50+</div>
            <div className="nh-proof-meta">Languages</div>
          </div>
        </div>

        <DownloadBar />
      </div>
    </section>
  );
};

/* ---------- Desktop download bar (Mac + Windows) ---------- */
// Installer URLs are env-driven so they can be swapped without code changes.
// Set REACT_APP_DOWNLOAD_MAC / REACT_APP_DOWNLOAD_WIN in the appropriate
// .env file (or replace the defaults to point at S3 / GitHub releases).
const DOWNLOAD_URLS = {
  mac: process.env.REACT_APP_DOWNLOAD_MAC || '/downloads/Terview.dmg',
  win: process.env.REACT_APP_DOWNLOAD_WIN || '/downloads/Terview-Setup.exe',
};

function detectOS() {
  if (typeof navigator === 'undefined') return null;
  const p = (navigator.userAgentData?.platform || navigator.platform || navigator.userAgent || '').toLowerCase();
  if (p.includes('mac')) return 'mac';
  if (p.includes('win')) return 'win';
  return null;
}

// Each guide is a list of sections so the modal can render headings + lists
// instead of one long ordered list. The audio-loopback section is critical
// for Auto Mode (Terview hearing the interviewer via system audio capture)
// — without BlackHole/VB-Cable, only your own mic is recorded.
const MAC_GUIDE = [
  {
    title: '1. Install the Terview app',
    steps: [
      'Click "Download for Mac" above — your browser saves Terview.dmg.',
      'Open the .dmg file from Finder or your Downloads folder.',
      'Drag the Terview icon into the Applications folder.',
      'On first launch, if macOS says "Terview can\'t be opened" — right-click the app in Applications, choose Open, then confirm.',
    ],
  },
  {
    title: '2. Grant permissions',
    steps: [
      'When prompted, allow Microphone access (for your voice).',
      'Allow Screen Recording (lets the assistant see code on your screen).',
      'You can manage both later in System Settings → Privacy & Security.',
    ],
  },
  {
    title: '3. Install BlackHole (required for Auto Mode)',
    intro: 'Auto Mode listens to the interviewer through your system audio. macOS doesn\'t expose system audio to apps directly, so you need a free virtual audio device called BlackHole.',
    steps: [
      <>Download BlackHole 2ch from <a href="https://existential.audio/blackhole/" target="_blank" rel="noopener noreferrer">existential.audio/blackhole</a> (free, open-source).</>,
      'Run the installer. You\'ll need to enter your Mac password.',
      'Open Audio MIDI Setup (search via Spotlight). Create a "Multi-Output Device" that contains both your speakers/headphones AND "BlackHole 2ch".',
      'In your meeting app (Zoom / Meet / Teams), set Speaker to that Multi-Output Device so the interviewer\'s audio reaches BlackHole.',
      'Terview will auto-detect BlackHole when you switch to Auto Mode.',
    ],
  },
  {
    title: '4. Sign in and you\'re ready',
    steps: [
      'Open Terview, sign in with your account, and pick Auto Mode in the AI Interview Assistant.',
      'You\'ll see "BlackHole 2ch" listed as the audio input — that means everything\'s wired up correctly.',
    ],
  },
];

const WIN_GUIDE = [
  {
    title: '1. Install the Terview app',
    steps: [
      'Click "Download for Windows" above — your browser saves Terview-Setup.exe.',
      'Open the installer from your Downloads folder.',
      'If Windows SmartScreen warns about the publisher, click "More info" → "Run anyway".',
      'Follow the installer wizard — the default install location is fine.',
      'Launch Terview from the Start Menu or desktop shortcut.',
    ],
  },
  {
    title: '2. Grant permissions',
    steps: [
      'Allow Microphone access when Windows prompts (for your voice).',
      'If you plan to share code, allow screen capture in Windows Settings → Privacy → Screen.',
    ],
  },
  {
    title: '3. Install VB-Cable (required for Auto Mode)',
    intro: 'Auto Mode listens to the interviewer through your system audio. Windows needs a free virtual audio device called VB-Cable so the meeting app\'s output can be routed into Terview.',
    steps: [
      <>Download VB-Cable from <a href="https://vb-audio.com/Cable/" target="_blank" rel="noopener noreferrer">vb-audio.com/Cable</a> (free for personal use).</>,
      'Unzip the archive, then right-click VBCABLE_Setup_x64.exe → Run as administrator.',
      'Click "Install Driver", then reboot when prompted.',
      'In Windows Sound settings, set the meeting app\'s playback device to "CABLE Input (VB-Audio Virtual Cable)" — so Zoom/Meet/Teams audio goes into the cable.',
      'In Windows Sound → Recording, make sure "CABLE Output" is enabled. Terview reads from this.',
    ],
  },
  {
    title: '4. Sign in and you\'re ready',
    steps: [
      'Open Terview, sign in, and pick Auto Mode in the AI Interview Assistant.',
      'You\'ll see "CABLE Output (VB-Audio Virtual Cable)" listed as the audio input — that\'s the confirmation everything\'s wired up.',
    ],
  },
];

function DownloadBar() {
  const detected = React.useMemo(detectOS, []);
  const [guide, setGuide] = React.useState(null); // 'mac' | 'win' | null

  // Lock body scroll while the install-guide modal is open.
  React.useEffect(() => {
    if (!guide) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setGuide(null); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [guide]);

  const handleDownload = (os) => {
    window.open(DOWNLOAD_URLS[os], '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <div className="nh-download nh-download-compact">
        <button
          type="button"
          className={`nh-dl-pill ${detected === 'mac' ? 'recommended' : ''}`}
          onClick={() => handleDownload('mac')}
        >
          <svg className="nh-dl-pill-icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
          </svg>
          <span className="nh-dl-pill-text">Download for Mac</span>
          <span
            role="button"
            tabIndex={0}
            className="nh-dl-pill-info"
            onClick={(e) => { e.stopPropagation(); setGuide('mac'); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                setGuide('mac');
              }
            }}
            aria-label="Mac installation guide"
            title="Installation guide"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
          </span>
        </button>

        <button
          type="button"
          className={`nh-dl-pill ${detected === 'win' ? 'recommended' : ''}`}
          onClick={() => handleDownload('win')}
        >
          <svg className="nh-dl-pill-icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            {/* Modern Windows 11 mark — clean 2x2 grid of squares. */}
            <rect x="3"  y="3"  width="8" height="8" />
            <rect x="13" y="3"  width="8" height="8" />
            <rect x="3"  y="13" width="8" height="8" />
            <rect x="13" y="13" width="8" height="8" />
          </svg>
          <span className="nh-dl-pill-text">Download for Windows</span>
          <span
            role="button"
            tabIndex={0}
            className="nh-dl-pill-info"
            onClick={(e) => { e.stopPropagation(); setGuide('win'); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                setGuide('win');
              }
            }}
            aria-label="Windows installation guide"
            title="Installation guide"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
          </span>
        </button>

      </div>

      {guide && createPortal(
        <div className="nh-dl-modal-bg" onClick={() => setGuide(null)} role="dialog" aria-modal="true">
          <div className="nh-dl-modal" onClick={(e) => e.stopPropagation()}>
            <div className="nh-dl-modal-head">
              <div className="nh-dl-modal-title">
                {guide === 'mac' ? 'Install Terview on macOS' : 'Install Terview on Windows'}
              </div>
              <button
                type="button"
                className="nh-dl-modal-close"
                onClick={() => setGuide(null)}
                aria-label="Close"
              >×</button>
            </div>
            <div className="nh-dl-modal-body">
              {(guide === 'mac' ? MAC_GUIDE : WIN_GUIDE).map((section, idx) => (
                <section key={idx} className="nh-dl-section">
                  <h4 className="nh-dl-section-title">{section.title}</h4>
                  {section.intro && (
                    <p className="nh-dl-section-intro">{section.intro}</p>
                  )}
                  <ul className="nh-dl-section-steps">
                    {section.steps.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </section>
              ))}
            </div>
            <div className="nh-dl-modal-foot">
              <button
                type="button"
                className="nh-btn nh-btn-primary"
                onClick={() => { handleDownload(guide); setGuide(null); }}
              >
                Download {guide === 'mac' ? 'for Mac' : 'for Windows'}
              </button>
              <button
                type="button"
                className="nh-btn nh-btn-ghost"
                onClick={() => setGuide(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

/* ---------- Trusted-by logos ---------- */
const TRUSTED_LOGOS = [
  { name: 'Google',    domain: 'google.com' },
  { name: 'Amazon',    domain: 'amazon.com' },
  { name: 'Microsoft', domain: 'microsoft.com' },
  { name: 'Apple',     domain: 'apple.com' },
  { name: 'Netflix',   domain: 'netflix.com' },
  { name: 'Meta',      domain: 'meta.com' },
  { name: 'LinkedIn',  domain: 'linkedin.com' },
  { name: 'Stripe',    domain: 'stripe.com' },
  { name: 'Airbnb',    domain: 'airbnb.com' },
  { name: 'Uber',      domain: 'uber.com' },
  { name: 'Salesforce',domain: 'salesforce.com' },
  { name: 'Adobe',     domain: 'adobe.com' },
  { name: 'Nvidia',    domain: 'nvidia.com' },
  { name: 'Tesla',     domain: 'tesla.com' },
  { name: 'Oracle',    domain: 'oracle.com' },
  { name: 'IBM',       domain: 'ibm.com' },
  { name: 'Intel',     domain: 'intel.com' },
  { name: 'Cisco',     domain: 'cisco.com' },
  { name: 'Spotify',   domain: 'spotify.com' },
  { name: 'Shopify',   domain: 'shopify.com' },
];
const TrustedBy = () => (
  <section className="nh-trusted">
    <p className="nh-trusted-title">Trusted by professionals at</p>
    <div className="nh-trusted-track-wrap">
      <div className="nh-trusted-track">
        {[...TRUSTED_LOGOS, ...TRUSTED_LOGOS].map((l, i) => (
          <div key={i} className="nh-trusted-logo" title={l.name}>
            {/* Colorful brand logo; Clearbit → Google favicon → hide if both fail */}
            <img
              src={`https://logo.clearbit.com/${l.domain}`}
              data-fb={`https://www.google.com/s2/favicons?domain=${l.domain}&sz=128`}
              alt={l.name}
              loading="lazy"
              onError={e => {
                const fb = e.currentTarget.getAttribute('data-fb');
                if (fb) { e.currentTarget.removeAttribute('data-fb'); e.currentTarget.src = fb; }
                else { const w = e.currentTarget.closest('.nh-trusted-logo'); if (w) w.style.display = 'none'; }
              }}
            />
            <span className="nh-trusted-name">{l.name}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- Core Features ---------- */
const CORE_FEATURES = [
  { title: 'Interview Copilot',  body: 'Real-time transcription + STAR-formatted answers fed to you as the question lands.', tag: 'Live',     path: '/InterviewAssistant' },
  { title: 'Mock Interview',      body: '15 personalized questions, full model answers, AI-graded delivery on 5 axes.',          tag: 'Practice', path: '/mockInterview' },
  { title: 'Resume Builder',      body: 'AI-rewritten bullets, ATS-optimized templates, downloadable in seconds.',                tag: 'ATS',      path: '/resume-builder' },
  { title: 'Cover Letter',        body: 'Company-specific opener, quantified hook from your resume, exported in 90 seconds.',     tag: 'Compose',  path: '/CoverLetter' },
  { title: 'ATS Check',           body: 'Single ATS Match score + per-keyword breakdown vs. the JD. One-click tailoring.',         tag: 'Score',    path: '/ATSCheck' },
  { title: 'Interview Questions', body: '50 personalized questions with full STAR-structured model answers — practice anywhere.',   tag: 'Drill',    path: '/InterviewQuestions' },
  { title: 'LinkedIn Optimizer',  body: 'AI-rewritten headline, summary, experience — tuned to recruiter searches. 5× more views.', tag: 'Visibility', path: '/LinkedInOptimizer' },
  { title: 'Auto Apply',          body: 'Real browser agent applies to LinkedIn, Indeed, Dice, Glassdoor on your behalf.',         tag: 'Agent',    path: '/AutoJobApply' },
  { title: 'Portfolio Builder',   body: 'Five hand-tuned templates, live WYSIWYG preview, sharable /p/handle link.',                tag: 'Showcase', path: '/portfolio' },
  { title: 'Course Creator',      body: 'AI-generated learning roadmap, day-by-day modules, quiz-gated mastery, verified cert.',    tag: 'Learn',    path: '/certification' },
];
const CoreFeatures = ({ onNav }) => {
  const gridRef = useRef(null);

  // Reveal each feature card as it enters the viewport. Left column cards
  // slide in from the LEFT, right column from the RIGHT. Row-based stagger
  // means row-1 lands together, then row-2, then row-3 — feels choreographed
  // instead of a flat "everything at once" pop. One-shot per card so a
  // scroll-back doesn't retrigger.
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const cards = Array.from(grid.querySelectorAll('.nh-feature-card'));
    if (!cards.length) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      cards.forEach((c) => c.classList.add('is-in'));
      return;
    }

    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          e.target.classList.add('is-in');
          io.unobserve(e.target);
        }
      }
    }, { root: null, threshold: 0.18, rootMargin: '0px 0px -6% 0px' });

    cards.forEach((c) => io.observe(c));
    return () => io.disconnect();
  }, []);

  return (
    <section className="nh-section" id="features">
      <div className="nh-section-head">
        <span className="nh-eyebrow">CORE FEATURES</span>
        <h2 className="nh-h2">Everything you need <span className="nh-grad-text">in the interview room</span>.</h2>
        <p className="nh-lead nh-center">
          Four flagship tools, one ecosystem. Each works independently — together, they're an unfair advantage.
        </p>
      </div>
      <div className="nh-feature-grid" ref={gridRef}>
        {CORE_FEATURES.map((f, i) => (
          <button
            key={f.title}
            type="button"
            className="nh-feature-card nh-feature-card--slide"
            data-side={i % 2 === 0 ? 'left' : 'right'}
            onClick={() => onNav(f.path)}
            style={{ '--tint': productTint(f.path) }}
          >
            <div className="nh-feature-icon" aria-hidden="true">
              <ProductIcon path={f.path} size={24} />
            </div>
            <span className="nh-feature-tag">{f.tag}</span>
            <h3>{f.title}</h3>
            <p>{f.body}</p>
            <span className="nh-feature-arrow">→</span>
          </button>
        ))}
      </div>
    </section>
  );
};

/* ---------- Setup steps ---------- */
const SETUP_STEPS = [
  { n: '01', title: 'Create your account', body: 'Sign up free in 30 seconds. No card required.' },
  { n: '02', title: 'Upload your resume', body: 'Or paste your profile. The AI grounds every answer in your real story.' },
  { n: '03', title: 'Practice or go live', body: 'Run mock interviews to drill, or share your live interview tab to get real-time help.' },
  { n: '04', title: 'Review & improve', body: 'Per-answer scoring, transcript playback, and gap analysis after every session.' },
];
const SetupSteps = ({ onCTA }) => {
  const ref = useRef(null);
  const [active, setActive] = useState(-1);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setActive(0); }),
      { threshold: 0.25 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  useEffect(() => {
    if (active < 0 || active >= SETUP_STEPS.length - 1) return;
    const t = setTimeout(() => setActive((a) => a + 1), 700);
    return () => clearTimeout(t);
  }, [active]);

  return (
    <section className="nh-section" ref={ref}>
      <div className="nh-section-head">
        <span className="nh-eyebrow">SIMPLE SETUP</span>
        <h2 className="nh-h2">From signup to interview-ready in <span className="nh-grad-text">90 seconds</span>.</h2>
      </div>
      <div className="nh-steps">
        {SETUP_STEPS.map((s, i) => (
          <div key={s.n} className={`nh-step ${i <= active ? 'lit' : ''}`}>
            <div className="nh-step-num">{s.n}</div>
            <h3>{s.title}</h3>
            <p>{s.body}</p>
            {i < SETUP_STEPS.length - 1 && <div className={`nh-step-link ${i < active ? 'lit' : ''}`} />}
          </div>
        ))}
      </div>
      <div className="nh-section-cta">
        <button className="nh-btn nh-btn-primary" onClick={onCTA}>Get started free</button>
      </div>
    </section>
  );
};

/* ---------- Desktop App section ---------- */
const DESKTOP_FEATURES = [
  { n: '01', title: 'Stealth mode', body: 'The assistant runs invisible to screen-share and recording software. Only you see it.' },
  { n: '02', title: 'Always-on overlay', body: 'Keep the panel above any meeting window. No tab-switching mid-question.' },
  { n: '03', title: 'Mouse-free shortcuts', body: 'Toggle, accept, regenerate — all from the keyboard. Eyes stay on the camera.' },
  { n: '04', title: 'IDE integrations', body: 'VSCode + Cursor extensions for live coding rounds. The hint sits inline.' },
];
const DesktopApp = () => (
  <section className="nh-section nh-desktop">
    <div className="nh-section-head">
      <span className="nh-eyebrow">DESKTOP APP</span>
      <h2 className="nh-h2">A native app built for <span className="nh-grad-text">live interviews</span>.</h2>
      <p className="nh-lead nh-center">
        Mac and Windows. Stealth from the meeting, instant from the keyboard.
      </p>
    </div>
    <div className="nh-desktop-grid">
      {DESKTOP_FEATURES.map((f) => (
        <div key={f.n} className="nh-desktop-card">
          <div className="nh-desktop-num">{f.n}</div>
          <h3>{f.title}</h3>
          <p>{f.body}</p>
        </div>
      ))}
    </div>
    <div className="nh-section-cta nh-desktop-cta">
      <a className="nh-btn nh-btn-primary" href="/downloads/TERVIEW-1.0.0-mac.dmg">
         Mac (.dmg)
      </a>
      <a className="nh-btn nh-btn-ghost" href="/downloads/TERVIEW Setup 1.0.0.exe">
         Windows (.exe)
      </a>
    </div>
  </section>
);

/* ---------- Why TERVIEW ---------- */
const WHY_CARDS = [
  { title: 'Sub-second latency', body: 'Suggestions render before you finish processing the question.', icon: '⚡' },
  { title: 'Multilingual', body: '50+ languages. Localized phrasing, idiom-aware.', icon: '🌐' },
  { title: 'All major LLMs', body: 'GPT-5, Claude Sonnet 4.6, Gemini, Llama — pick your model.', icon: '🧠' },
  { title: 'Career OS, not just an app', body: 'Resume, ATS, cover letter, portfolio, mock — one connected suite.', icon: '🛰️' },
  { title: 'Practice anywhere', body: 'Web, desktop, IDE. Sessions sync across all of them.', icon: '🪐' },
  { title: 'Built by interviewers', body: 'Every feature shipped by people who hired and got hired here.', icon: '🛠️' },
];
const WhyTerview = () => (
  <section className="nh-section">
    <div className="nh-section-head">
      <span className="nh-eyebrow">WHY TERVIEW</span>
      <h2 className="nh-h2">The most <span className="nh-grad-text">advanced</span> interview platform.</h2>
      <p className="nh-lead nh-center">
        Backed by every cutting-edge model, built for every interview format, designed for every candidate.
      </p>
    </div>
    <div className="nh-why-grid">
      {WHY_CARDS.map((c) => (
        <div key={c.title} className="nh-why-card">
          <div className="nh-why-icon">{c.icon}</div>
          <h3>{c.title}</h3>
          <p>{c.body}</p>
        </div>
      ))}
    </div>
  </section>
);

/* ---------- Scenarios ---------- */
const SCENARIOS = [
  { title: 'System Design', body: 'Sketch architectures live with the AI suggesting components, tradeoffs, and edge cases.' },
  { title: 'Behavioral / STAR', body: 'Story scaffolds drawn from your resume, structured Situation→Task→Action→Result.' },
  { title: 'Software Engineering', body: 'DSA hints, complexity analysis, and idiomatic-language suggestions for your stack.' },
  { title: 'Product Manager', body: 'Frameworks (CIRCLES, RICE, AARRR) suggested per question, customized to the role.' },
  { title: 'Sales / Customer Success', body: 'Discovery prompts, objection-handling, and competitor positioning live.' },
  { title: 'Consulting Cases', body: 'MECE breakdowns, hypothesis trees, and number sense support during case interviews.' },
];
const Scenarios = () => (
  <section className="nh-section">
    <div className="nh-section-head">
      <span className="nh-eyebrow">SCENARIOS</span>
      <h2 className="nh-h2">Supports <span className="nh-grad-text">every interview format</span>.</h2>
      <p className="nh-lead nh-center">
        From algorithm rounds to executive coffee chats. The assistant adapts to your exact format and industry.
      </p>
    </div>
    <div className="nh-scenarios-grid">
      {SCENARIOS.map((s) => (
        <div key={s.title} className="nh-scenario">
          <h3>{s.title}</h3>
          <p>{s.body}</p>
        </div>
      ))}
    </div>
  </section>
);

/* ---------- Integrations ---------- */
const INTEGRATIONS = ['Zoom', 'Google Meet', 'Microsoft Teams', 'Webex', 'BlueJeans', 'HireVue', 'CodeSignal', 'HackerRank'];
const Integrations = () => (
  <section className="nh-section nh-integrations">
    <div className="nh-section-head">
      <span className="nh-eyebrow">INTEGRATIONS</span>
      <h2 className="nh-h2">Works with <span className="nh-grad-text">every meeting platform</span>.</h2>
      <p className="nh-lead nh-center">
        We capture system audio, so anything you can share, we can support.
      </p>
    </div>
    <div className="nh-integrations-grid">
      {INTEGRATIONS.map((p) => (
        <div key={p} className="nh-integration">{p}</div>
      ))}
    </div>
  </section>
);

/* ---------- Testimonials ---------- */
const TESTIMONIALS = [
  { name: 'Sarah Mitchell',   role: 'SWE @ Google',   text: 'I walked into the Google final feeling like I had been there before. The Copilot turned every question into a STAR I already had a draft of.' },
  { name: 'Michael Chen',     role: 'PM @ Amazon',    text: 'The mock interview drilled me on 15 questions in an hour. The grading was sharper than my actual hiring committee.' },
  { name: 'Emily Rodriguez',  role: 'DS @ Microsoft', text: 'For coding rounds, the inline hints in VSCode never gave away the answer — they kept me unblocked. Exactly the right level.' },
  { name: 'David Thompson',   role: 'UX @ Apple',     text: 'I went from "I never get past phone screens" to four onsites in two months. The resume tailoring is what unlocked it.' },
];
const Testimonials = () => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % TESTIMONIALS.length), 5500);
    return () => clearInterval(t);
  }, []);
  return (
    <section className="nh-section">
      <div className="nh-section-head">
        <span className="nh-eyebrow">TESTIMONIALS</span>
        <h2 className="nh-h2">Real results from <span className="nh-grad-text">real candidates</span>.</h2>
      </div>
      <div className="nh-testimonial-card">
        <div className="nh-testimonial-stars">★★★★★</div>
        <p className="nh-testimonial-text">"{TESTIMONIALS[idx].text}"</p>
        <div className="nh-testimonial-meta">
          <span className="nh-testimonial-name">{TESTIMONIALS[idx].name}</span>
          <span className="nh-testimonial-role">{TESTIMONIALS[idx].role}</span>
        </div>
        <div className="nh-testimonial-dots">
          {TESTIMONIALS.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`nh-test-dot ${i === idx ? 'active' : ''}`}
              aria-label={`Go to testimonial ${i + 1}`}
              onClick={() => setIdx(i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Comparison table ---------- */
const COMPARE_ROWS = [
  { feature: 'Real-time answers in interviews', terview: true,  generic: false, traditional: false },
  { feature: 'STAR-formatted from your resume',  terview: true,  generic: false, traditional: false },
  { feature: 'Coding round hints (live)',         terview: true,  generic: false, traditional: false },
  { feature: 'AI-graded mock interviews',         terview: true,  generic: false, traditional: 'limited' },
  { feature: '50+ languages',                     terview: true,  generic: 'limited', traditional: false },
  { feature: 'Auto-applies to job boards',        terview: true,  generic: false, traditional: false },
  { feature: 'Resume / ATS / cover letter suite', terview: true,  generic: false, traditional: false },
  { feature: 'Free plan',                          terview: true,  generic: 'limited', traditional: false },
];
const ComparisonCell = ({ value }) => {
  if (value === true)        return <span className="nh-cell yes">✓</span>;
  if (value === false)       return <span className="nh-cell no">—</span>;
  if (value === 'limited')   return <span className="nh-cell mid">partial</span>;
  return <span className="nh-cell">{value}</span>;
};
const Comparison = () => (
  <section className="nh-section">
    <div className="nh-section-head">
      <span className="nh-eyebrow">COMPARISON</span>
      <h2 className="nh-h2">Not all AI tools are <span className="nh-grad-text">the same</span>.</h2>
      <p className="nh-lead nh-center">
        How TERVIEW stacks up against generic AI chatbots and traditional interview prep.
      </p>
    </div>
    <div className="nh-compare-card">
      <div className="nh-compare-row nh-compare-head">
        <div className="nh-compare-feat" />
        <div className="nh-compare-col nh-compare-col-us">TERVIEW</div>
        <div className="nh-compare-col">Generic AI tools</div>
        <div className="nh-compare-col">Traditional prep</div>
      </div>
      {COMPARE_ROWS.map((r) => (
        <div key={r.feature} className="nh-compare-row">
          <div className="nh-compare-feat">{r.feature}</div>
          <div className="nh-compare-col nh-compare-col-us"><ComparisonCell value={r.terview} /></div>
          <div className="nh-compare-col"><ComparisonCell value={r.generic} /></div>
          <div className="nh-compare-col"><ComparisonCell value={r.traditional} /></div>
        </div>
      ))}
    </div>
  </section>
);

/* ---------- Pricing teaser ---------- */
const Pricing = ({ onCTA }) => (
  <section className="nh-section">
    <div className="nh-section-head">
      <span className="nh-eyebrow">PRICING</span>
      <h2 className="nh-h2">Is TERVIEW free? <span className="nh-grad-text">Yes.</span></h2>
      <p className="nh-lead nh-center">
        Core tools are free forever. Upgrade for unlimited live sessions, premium models, and the desktop app.
      </p>
    </div>
    <div className="nh-pricing-grid">
      <div className="nh-price-card">
        <div className="nh-price-head">Free</div>
        <div className="nh-price-num">$0</div>
        <div className="nh-price-sub">forever · for everyone</div>
        <ul className="nh-price-list">
          <li>5 mock interviews / month</li>
          <li>Resume Builder + 4 templates</li>
          <li>ATS Check unlimited</li>
          <li>Cover Letter generator</li>
          <li>Portfolio Builder</li>
        </ul>
        <button className="nh-btn nh-btn-ghost" onClick={onCTA}>Start free</button>
      </div>
      <div className="nh-price-card nh-price-card-featured">
        <div className="nh-price-badge">Most popular</div>
        <div className="nh-price-head">Pro</div>
        <div className="nh-price-num">$29<span>/mo</span></div>
        <div className="nh-price-sub">monthly · cancel anytime</div>
        <ul className="nh-price-list">
          <li>Unlimited live Interview Copilot</li>
          <li>Unlimited mock interviews</li>
          <li>Desktop stealth app (Mac + Win)</li>
          <li>VSCode + Cursor IDE integration</li>
          <li>Premium LLMs (GPT-5, Sonnet 4.6)</li>
          <li>Auto-Apply agent (50 jobs/day)</li>
        </ul>
        <button className="nh-btn nh-btn-primary" onClick={onCTA}>Start Pro free for 7 days</button>
      </div>
    </div>
  </section>
);

/* ---------- FAQ ---------- */
const FAQS = [
  { q: 'Will the interviewer see the assistant?', a: 'No. The assistant runs inside your tab or desktop app — never on the meeting itself. Stealth mode hides it from screen-share APIs.' },
  { q: 'How fast are the suggestions?', a: 'Average end-to-end latency is 116ms — usually faster than your own thought formation. By the time you start answering, you already have the structure.' },
  { q: 'Does it work for coding rounds?', a: 'Yes. Use our VSCode/Cursor extensions for live inline hints. We surface algorithm direction and complexity, never the literal solution.' },
  { q: 'What languages are supported?', a: '50+ spoken languages with native-speaker phrasing, plus all major programming languages.' },
  { q: 'Is my interview audio stored?', a: 'No. Audio is transcribed in-stream and discarded the moment the session ends, unless you explicitly save the transcript.' },
  { q: 'How is this different from ChatGPT?', a: 'ChatGPT requires you to paste questions and read outputs. TERVIEW listens, knows your resume + the JD, and surfaces a fully-structured answer in real time without you typing a thing.' },
];
const FAQAccordion = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="nh-section">
      <div className="nh-section-head">
        <span className="nh-eyebrow">SUPPORT</span>
        <h2 className="nh-h2">Frequently asked <span className="nh-grad-text">questions</span></h2>
      </div>
      <div className="nh-faq-list">
        {FAQS.map((f, i) => (
          <button key={f.q} type="button" className={`nh-faq-item ${open === i ? 'open' : ''}`} onClick={() => setOpen(open === i ? -1 : i)}>
            <div className="nh-faq-q">
              <span>{f.q}</span>
              <span className="nh-faq-chev">+</span>
            </div>
            <div className="nh-faq-a"><p>{f.a}</p></div>
          </button>
        ))}
      </div>
    </section>
  );
};

/* ---------- Final CTA ---------- */
const FinalCTA = ({ onCTA }) => (
  <section className="nh-cta-final">
    <div className="nh-cta-card">
      <div className="nh-cta-glow" />
      <h2 className="nh-h2">
        Your next interview could be <span className="nh-grad-text">the one</span>.
      </h2>
      <p>Join 1M+ professionals who landed their dream job with a co-pilot in their ear. Start in 90 seconds.</p>
      <div className="nh-hero-cta center">
        <button className="nh-btn nh-btn-primary" onClick={onCTA}>
          Start free
          <svg width="16" height="16" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7"/></svg>
        </button>
      </div>
    </div>
  </section>
);

/* ---------- Page root ---------- */
const NeuralHomepage = () => {
  const navigate = useNavigate();
  const [showAuthModal, setShowAuthModal] = useState(false);
  // Same global `studioTheme` the studios read, so the choice carries over
  // from a studio to the marketing page and back.
  const themeClass = useStudioTheme();

  // Auto-open the modal if any landing page redirected here with ?openLogin=1.
  // Strip the query so reload doesn't re-trigger.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    // Logout entry point (used by the browser-extension side panel). Clear the
    // session and hard-reload so UserContext re-reads the (now empty) token.
    if (params.get('logout') === '1') {
      localStorage.removeItem('token');
      localStorage.removeItem('userData');
      params.delete('logout');
      window.location.replace(`${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`);
      return;
    }
    if (params.get('openLogin') === '1') {
      setShowAuthModal(true);
      params.delete('openLogin');
      const cleaned = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
      window.history.replaceState({}, '', cleaned);
    }
    // Open the pricing modal (used by the browser-extension side panel).
    if (params.get('openPricing') === '1') {
      // Defer so Header's `openPricingModal` listener is mounted first.
      setTimeout(() => document.dispatchEvent(new CustomEvent('openPricingModal')), 0);
      params.delete('openPricing');
      const cleaned = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
      window.history.replaceState({}, '', cleaned);
    }
  }, []);

  // Always navigate directly. Destination pages own their auth/credit gating.
  const onCTA = useCallback(() => navigate('/InterviewAssistant'), [navigate]);
  const onNav = useCallback((path) => navigate(path), [navigate]);

  return (
    <div className={`nh-page ${themeClass}`}>
      <Seo
        title="Terview AI - Professional AI Career Copilot"
        description="Terview AI is an AI-powered career copilot: real-time interview help, mock interviews, AI resume builder, ATS checker, cover letters, and auto job apply."
        path="/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'Terview AI',
          url: 'https://terview.ai/',
          potentialAction: {
            '@type': 'SearchAction',
            target: 'https://terview.ai/blog?q={search_term_string}',
            'query-input': 'required name=search_term_string',
          },
        }}
      />
      <Header setShowAuthModal={setShowAuthModal} />
      {/* Parks beside the header pill — see `.cls-theme-dock` in studioTheme.css. */}

      <Hero onCTA={onCTA} onNav={onNav} />
      <TrustedBy />
      <CoreFeatures onNav={onNav} />
      <SetupSteps onCTA={onCTA} />
      <DesktopApp />
      <WhyTerview />
      <Scenarios />
      <Integrations />
      <Testimonials />
      <Comparison />
      <Pricing onCTA={onCTA} />
      <FAQAccordion />
      <FinalCTA onCTA={onCTA} />

      <Footer />

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </div>
  );
};

export default NeuralHomepage;
